<template>
  <div class="non-core-auto-order-settings">
    <div class="row px-3 pb-3 mt-2 ml-1">
      <hr>
      <div class="text-22 text-bold">
        {{ $t("flexben.clients.nonCoreAutomaticOrders.title") }}
      </div>
    </div>

    <!-- Settings Grid -->
    <div class="row px-3 pb-3">
      <div
        v-for="setting in localSettings"
        :key="setting.id"
        class="col-md-6 mb-3"
      >
        <div class="card p-3">
          <div class="d-flex align-items-center">
          <b-form-checkbox
            class="mb-3"
            :id="`toggle-active-${setting.id}`"
            v-model="setting.is_active"
            switch
            @change="onSettingsChange"
          />
            <label
              :for="`toggle-active-${setting.id}`"
              class="mb-3 ml-3 cursor-pointer"
            >
              {{ $t("flexben.clients.nonCoreAutomaticOrders.activate_roi_flow") }}
            </label>
          </div>
          <h5 class="text-bold mb-3">
            {{ $t("flexben.clients.nonCoreAutomaticOrders."+setting.benefit_type) }}
          </h5>

          <div class="mb-3">
            <div class="d-flex align-items-center mb-2">
              <b-form-checkbox
                :id="`period-prev-month-${setting.id}`"
                switch
                :checked="isPrevCalendarMonth(setting)"
                :disabled="!setting.is_active"
                @change="onPresetPeriodChange(setting, 'prev_month')"
              />
              <label
                :for="`period-prev-month-${setting.id}`"
                class="mb-0 ml-3 cursor-pointer"
              >
                {{ $t("flexben.clients.nonCoreAutomaticOrders.prev_calendar_month") }}
              </label>
              <span
                :id="`prev-month-info-${setting.id}`"
                class="d-inline-block flexben-tooltip-icon ml-2 text-bold"
              >i</span>
              <b-tooltip :target="`prev-month-info-${setting.id}`" variant="flexben" placement="topleft">
                <div class="text-left">{{ $t("flexben.clients.nonCoreAutomaticOrders.info_prev_calendar_month") }}</div>
              </b-tooltip>
            </div>
            <div class="d-flex align-items-center mb-2">
              <b-form-checkbox
                :id="`period-shifted-${setting.id}`"
                switch
                :checked="isShiftedCalendarMonth(setting)"
                :disabled="!setting.is_active"
                @change="onPresetPeriodChange(setting, 'shifted_15_14')"
              />
              <label
                :for="`period-shifted-${setting.id}`"
                class="mb-0 ml-3 cursor-pointer"
              >
                {{ $t("flexben.clients.nonCoreAutomaticOrders.shifted_calendar_month") }}
              </label>
              <span
                :id="`shifted-info-${setting.id}`"
                class="d-inline-block flexben-tooltip-icon ml-2 text-bold"
              >i</span>
              <b-tooltip :target="`shifted-info-${setting.id}`" variant="flexben" placement="topleft">
                <div class="text-left">{{ $t("flexben.clients.nonCoreAutomaticOrders.info_shifted_calendar_month") }}</div>
              </b-tooltip>
            </div>
            <div class="d-flex align-items-center">
              <b-form-checkbox
                :id="`period-custom-${setting.id}`"
                switch
                :checked="isCustomPeriod(setting)"
                :disabled="!setting.is_active"
                @change="onPresetPeriodChange(setting, 'custom')"
              />
              <label
                :for="`period-custom-${setting.id}`"
                class="mb-0 ml-3 cursor-pointer"
              >
                {{ $t("flexben.clients.nonCoreAutomaticOrders.custom_period") }}
              </label>
            </div>
          </div>

          <!-- Period Days Row -->
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label class="col-form-label pt-0">
                  {{ $t("flexben.clients.nonCoreAutomaticOrders.start_day") }}
                </label>
                <div class="d-flex align-items-center mb-2">
                  <b-form-checkbox
                    :id="`start-prev-month-${setting.id}`"
                    v-model="setting.start_is_prev_month"
                    switch
                    :disabled="!setting.is_active || !isCustomPeriod(setting)"
                    @change="onManualPeriodFieldChange(setting)"
                  >
                  </b-form-checkbox>
                  <label
                    :for="`start-prev-month-${setting.id}`"
                    class="mb-0 ml-3 cursor-pointer"
                  >
                    {{ $t("flexben.clients.nonCoreAutomaticOrders.start_is_prev_month") }}
                  </label>
                </div>
                <select
                  class="form-control"
                  v-model.number="setting.start_day"
                  :disabled="!setting.is_active || !isCustomPeriod(setting)"
                  @change="onManualPeriodFieldChange(setting)"
                >
                  <option
                    v-for="day in dayOptions"
                    :key="day.value"
                    :value="day.value"
                  >
                    {{ day.text }}
                  </option>
                </select>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label class="col-form-label pt-0">
                  {{ $t("flexben.clients.nonCoreAutomaticOrders.end_day") }}
                </label>
                <div class="d-flex align-items-center mb-2">
                  <b-form-checkbox
                    :id="`end-prev-month-${setting.id}`"
                    v-model="setting.end_is_prev_month"
                    switch
                    :disabled="!setting.is_active || !isCustomPeriod(setting)"
                    @change="onManualPeriodFieldChange(setting)"
                  >
                  </b-form-checkbox>
                  <label
                    :for="`end-prev-month-${setting.id}`"
                    class="mb-0 ml-3 cursor-pointer"
                  >
                    {{ $t("flexben.clients.nonCoreAutomaticOrders.end_is_prev_month") }}
                  </label>
                </div>
                <select
                  class="form-control"
                  v-model.number="setting.end_day"
                  :disabled="!setting.is_active || !isCustomPeriod(setting)"
                  @change="onManualPeriodFieldChange(setting)"
                >
                  <option
                    v-for="day in dayOptions"
                    :key="day.value"
                    :value="day.value"
                  >
                    {{ day.text }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="align-content-center mb-0">
            <h6 class=" pt-0">
              {{ $t("flexben.clients.nonCoreAutomaticOrders.order_day") }}
            </h6>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BTooltip } from 'bootstrap-vue';

export default {
  components: {
    'b-tooltip': BTooltip
  },
  props: {
    settings: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      localSettings: [],
      availableBenefitTypes: ['ppp3', 'medical_insurance']
    }
  },

  computed: {
    dayOptions() {
      const options = [
        {
          value: -1,
          text: this.$t("flexben.clients.nonCoreAutomaticOrders.last_day_of_month")
        }
      ];

      for (let i = 1; i <= 31; i++) {
        options.push({
          value: i,
          text: i.toString()
        });
      }

      return options;
    }
  },

  methods: {
    getPeriodType(setting) {
      const isPrevMonthStart = setting.start_is_prev_month === true;
      const isPrevMonthEnd = setting.end_is_prev_month === true;

      if (
        isPrevMonthStart &&
        isPrevMonthEnd &&
        setting.start_day === 1 &&
        setting.end_day === -1
      ) {
        return 'prev_month';
      }

      if (
        isPrevMonthStart &&
        setting.end_is_prev_month === false &&
        setting.start_day === 15 &&
        setting.end_day === 14
      ) {
        return 'shifted_15_14';
      }

      return 'custom';
    },

    isPrevCalendarMonth(setting) {
      return this.getPeriodType(setting) === 'prev_month';
    },

    isShiftedCalendarMonth(setting) {
      return this.getPeriodType(setting) === 'shifted_15_14';
    },

    isCustomPeriod(setting) {
      return this.getPeriodType(setting) === 'custom';
    },

    applyPeriodPreset(setting, type) {
      if (type === 'prev_month') {
        setting.start_is_prev_month = true;
        setting.start_day = 1;
        setting.end_is_prev_month = true;
        setting.end_day = -1;
        setting.order_day = 15;
      } else if (type === 'shifted_15_14') {
        setting.start_is_prev_month = true;
        setting.start_day = 15;
        setting.end_is_prev_month = false;
        setting.end_day = 14;
        setting.order_day = 15;
      } else if (type === 'custom') {
        setting.start_is_prev_month = false;
        setting.end_is_prev_month = false;
        setting.start_day = 1;
        setting.end_day = 1;
        setting.order_day = 15;
      }

      this.onSettingsChange();
    },

    onPresetPeriodChange(setting, type) {
      this.applyPeriodPreset(setting, type);
    },

    onManualPeriodFieldChange(setting) {
      this.onSettingsChange();
    },

    onSettingsChange() {
      console.log('onSettingsChange()', this.localSettings);
      console.log('settings prop:', this.settings);
      this.$emit('update:settings', this.localSettings);
    },

    initializeSettings() {
      const newLocalSettings = this.availableBenefitTypes.map(benefitType => {
        const existingSetting = this.settings.find(s => s.benefit_type === benefitType);

        if (existingSetting) {
          return { ...existingSetting };
        } else {
          return {
            id: 'new_' + benefitType,
            benefit_type: benefitType,
            start_day: 1,
            start_is_prev_month: false,
            end_day: 1,
            end_is_prev_month: false,
            order_day: 15,
            is_active: false,
            is_new: true
          };
        }
      });
      this.localSettings = newLocalSettings;
    }
  },
  watch: {
    settings: {
      handler() {
        this.initializeSettings();
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.onSettingsChange = this.onSettingsChange.bind(this);
  }
}
</script>
